import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "@/components/ui/use-toast";
import type { FleetCapacityByType } from "./utils";
import { checkPublishedBreakConflicts, type PublishedBreakInput } from "./breakSchedulerUtils";

const DEFAULT_MAX_HOURS = 7;
const DEFAULT_BREAK_MINUTES = 45;

/**
 * Must run independently of whether the Breaks sub-tab is mounted -- a
 * real booking can land on a published break at any time, but this used to
 * live entirely inside BreaksTab.tsx, so it only ever fired while a
 * dispatcher happened to have that tab open. In practice dispatchers watch
 * the main slot sheet, so breaks silently never moved. Runs at the page
 * level instead, on the same poll cadence dataByType already has. Returns
 * shift_ids whose published break is blocked with no room left to push it,
 * for BreaksTab's "Overdue" badge.
 */
export function useBreakConflictAutoPush(
  dataByType: Record<string, FleetCapacityByType>,
  dateStr: string
): Set<string> {
  const [maxHours, setMaxHours] = useState(DEFAULT_MAX_HOURS);
  const [breakMinutes, setBreakMinutes] = useState(DEFAULT_BREAK_MINUTES);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [publishedBreaks, setPublishedBreaks] = useState<PublishedBreakInput[]>([]);
  const [stuckShiftIds, setStuckShiftIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    supabase
      .from("settings")
      .select("name, value")
      .in("name", ["fleet_break_max_hours", "fleet_break_minutes"])
      .then(({ data }) => {
        const byName = new Map((data || []).map((s) => [s.name, s.value]));
        const h = parseInt(String(byName.get("fleet_break_max_hours") ?? ""), 10);
        const m = parseInt(String(byName.get("fleet_break_minutes") ?? ""), 10);
        if (!Number.isNaN(h) && h > 0) setMaxHours(h);
        if (!Number.isNaN(m) && m > 0) setBreakMinutes(m);
        setSettingsLoaded(true);
      });
  }, []);

  const loadPublishedBreaks = useCallback(async () => {
    // taken=true is excluded outright, not just skipped when checking for
    // conflicts -- once a dispatcher confirms the break actually happened
    // (even a few minutes early/late), it's locked from being bounced
    // around by a booking that lands nearby afterwards.
    const { data } = await supabase
      .from("roster_scheduled_breaks")
      .select("id, shift_id, vehicle_number, suggested_start_hhmm, confirmed_start_hhmm")
      .eq("break_date", dateStr)
      .eq("status", "published")
      .eq("taken", false);
    setPublishedBreaks(
      (data || [])
        .filter((b) => b.vehicle_number)
        .map((b) => ({
          id: b.id,
          shiftId: b.shift_id,
          vehicleNumber: b.vehicle_number,
          startHHMM: b.confirmed_start_hhmm || b.suggested_start_hhmm,
        }))
    );
  }, [dateStr]);

  useEffect(() => {
    loadPublishedBreaks();
  }, [loadPublishedBreaks]);

  useEffect(() => {
    if (!settingsLoaded) return;
    if (publishedBreaks.length === 0) {
      setStuckShiftIds(new Set());
      return;
    }

    const resolutions = checkPublishedBreakConflicts(publishedBreaks, dataByType, dateStr, maxHours, breakMinutes);
    setStuckShiftIds(new Set(resolutions.filter((r) => !r.canPush).map((r) => r.shiftId)));

    const pushable = resolutions.filter((r) => r.canPush);
    if (pushable.length === 0) return;

    Promise.all(
      pushable.map((r) =>
        supabase
          .from("roster_scheduled_breaks")
          .update({ confirmed_start_hhmm: r.newStartHHMM, confirmed_end_hhmm: r.newEndHHMM, updated_at: new Date().toISOString() })
          .eq("id", r.id)
      )
    ).then(() => {
      toast({
        title: "Break rescheduled",
        description: `${pushable.length} break${pushable.length === 1 ? "" : "s"} moved to the next open slot after a booking landed on top of it.`,
      });
      loadPublishedBreaks();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataByType, publishedBreaks, dateStr, maxHours, breakMinutes, settingsLoaded]);

  return stuckShiftIds;
}
